import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

export function structuredCloneWithFallback<T>(value: T): T {
  return typeof window.structuredClone === "function"
    ? window.structuredClone(value)
    : JSON.parse(JSON.stringify(value));
}

export async function wait(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Extract the current full path (pathname + search) from Next.js headers.
 * Prioritizes X-Forwarded-Uri (set by middleware), then falls back to referer header.
 *
 * @param headersList - The headers object from Next.js `headers()`
 * @returns The full path (e.g., "/dashboard/projects?sort=name")
 */
export function getFullPathFromHeaders(headersList: Headers): string {
  // First, check for X-Forwarded-Uri set by our middleware
  const forwardedUri = headersList.get("X-Forwarded-Uri");
  if (forwardedUri) {
    return forwardedUri;
  }

  // Fallback to referer header
  const referer = headersList.get("referer") || "";
  if (referer) {
    try {
      const url = new URL(referer);
      return url.pathname + url.search;
    } catch {
      // Fallback if referer parsing fails
      return "/";
    }
  }

  return "/";
}

export function retry<T>(
  callback: ({ attempt }: { attempt: number }) => T,
  { attempts = 3, millis = 300, failSilently = false }: { attempts?: number; failSilently?: boolean; millis?: number }
): () => Promise<T> {
  let attempt = 0;
  let error: unknown;

  return () =>
    new Promise((resolve, reject) => {
      function tryAgain() {
        attempt++;

        if (attempt > attempts) {
          if (!failSilently) {
            reject(error);
          }
        } else {
          setTimeout(async () => {
            try {
              const result = await callback({ attempt });

              resolve(result);
            } catch (e) {
              error = e;
              tryAgain();
            }
          }, millis);
        }
      }

      attempt = 0;
      tryAgain();
    });
}

export const dateFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
});

export const datetimeFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "short",
});

export function formatPhoneNumber(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  const national = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (national.length === 10) {
    return `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`;
  }
  return raw;
}
